"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import {
  Clock,
  Copyright,
  FileText,
  Lightbulb,
  Shield,
  CircleDot,
} from "lucide-react";
import { PublicLink as Link } from "@/components/public/PublicLink";

export default function IntellectualPropertyPage() {
  const { t } = useJsonTranslation("IntellectualProperty");

  return (
    <div className="container mx-auto px-4 py-8 pt-24 sm:py-16">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="bg-leaf-500/20 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl">
            <Shield className="text-leaf-600 dark:text-leaf-400 h-8 w-8" />
          </div>
          <h1 className="text-foreground mb-4 text-4xl font-bold">
            {t("title")}
          </h1>
          <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{t("lastUpdated")}</span>
            </div>
          </div>
        </div>

        {/* Patent Notice */}
        <Card
          variant="glass"
          className="from-leaf-500/10 to-leaf-500/10 border-leaf-500/30 mb-8 bg-gradient-to-r"
        >
          <CardContent className="pt-6 text-center">
            <Lightbulb className="text-leaf-600 dark:text-leaf-400 mx-auto mb-4 h-8 w-8" />
            <h2 className="text-foreground mb-2 text-xl font-bold">
              {t("sections.patent")}
            </h2>
            <p className="text-muted-foreground">{t("content.patent.p")}</p>
          </CardContent>
        </Card>

        {/* Content */}
        <div className="space-y-8">
          {/* Trademarks */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CircleDot className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                {t("sections.trademarks")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.trademarks.p")}</p>
              <div className="bg-foreground/5 rounded-lg p-4">
                <h5 className="text-foreground mb-2 font-medium">
                  {t("content.trademarks.protectedTitle")}
                </h5>
                <ul className="space-y-1 text-sm">
                  {[0, 1, 2, 3].map((i) => (
                    <li key={i}>• {t(`content.trademarks.marks.${i}`)}</li>
                  ))}
                </ul>
              </div>
              <p>{t("content.trademarks.p2")}</p>
            </CardContent>
          </Card>

          {/* Copyright */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Copyright className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                {t("sections.copyright")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.copyright.p")}</p>
              <Separator />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="bg-foreground/5 rounded-lg p-4">
                  <h5 className="text-foreground mb-2 font-medium">
                    {t("content.copyright.youMay")}
                  </h5>
                  <ul className="space-y-1 text-sm">
                    {[0, 1, 2].map((i) => (
                      <li key={i}>
                        • {t(`content.copyright.youMayItems.${i}`)}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-foreground/5 rounded-lg p-4">
                  <h5 className="text-foreground mb-2 font-medium">
                    {t("content.copyright.youMayNot")}
                  </h5>
                  <ul className="space-y-1 text-sm">
                    {[0, 1, 2].map((i) => (
                      <li key={i}>
                        • {t(`content.copyright.youMayNotItems.${i}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* User Content */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                {t("sections.userContent")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.userContent.p1")}</p>
              <p>{t("content.userContent.p2")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-leaf-400">•</span>
                    <span>{t(`content.userContent.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Software */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                {t("sections.software")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.software.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-leaf-400">•</span>
                    <span>{t(`content.software.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Enforcement */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                {t("sections.enforcement")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.enforcement.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-leaf-600 dark:text-leaf-400">•</span>
                    <span>{t(`content.enforcement.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                {t("content.enforcement.p2")}{" "}
                <a
                  href="mailto:legal@vidseeds.ai"
                  className="text-leaf-600 dark:text-leaf-400 hover:underline"
                >
                  legal@vidseeds.ai
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card variant="glass">
            <CardContent className="pt-6 text-center">
              <h3 className="text-foreground mb-2 text-xl font-semibold">
                {t("sections.questions")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("content.questions.p")}
              </p>
              <Link href="/contact">
                <Button className="bg-leaf-500 hover:bg-leaf-600">
                  {t("content.questions.button")}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      <ScrollToTop />
    </div>
  );
}
